import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, EnvelopeSimple, MapPin, Truck, Invoice, Percent, FacebookLogo, InstagramLogo, YoutubeLogo, TiktokLogo, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const PAYMENTS = ["VISA", "Mastercard", "Maestro", "Apple Pay", "Google Pay", "Swedbank", "SEB", "Citadele", "Luminor"];
const CARRIERS = ["DPD", "Omniva", "Venipak", "Latvijas Pasts", "Express Pasts"];
const SOCIALS = [
  { icon: FacebookLogo, label: "Facebook", href: "https://facebook.com" },
  { icon: InstagramLogo, label: "Instagram", href: "https://instagram.com" },
  { icon: YoutubeLogo, label: "YouTube", href: "https://youtube.com" },
  { icon: TiktokLogo, label: "TikTok", href: "https://tiktok.com" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await api.post("/newsletter", { email: email.trim() });
      toast.success("Demo pieteikums saglabāts šajā pārlūkā.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <footer className="bg-[#1D1D1F] text-white mt-16">
      <div className="border-b border-white/10 bg-[#F4F8FC]">
        <div className="mx-auto max-w-7xl px-4 py-8 flex flex-col md:flex-row md:items-center gap-6">
          <div className="md:max-w-xs">
            <p className="font-heading text-lg font-bold text-[#1D1D1F]">Pierakstieties jaunumiem</p>
            <p className="mt-0.5 text-xs text-slate-500">Akcijas, jaunas preces un padomi — ne biežāk kā reizi nedēļā.</p>
            <p className="mt-1 text-[11px] text-slate-400">Demo: e-pasts netiks nosūtīts ārpus šīs pārlūkprogrammas.</p>
          </div>
          <form onSubmit={subscribe} className="flex w-full flex-1 max-w-xl flex-col sm:flex-row gap-3">
            <input data-testid="newsletter-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="E-pasts" className="w-full flex-1 rounded-full border border-slate-300 bg-white px-5 py-3 text-sm text-[#1D1D1F] focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <button data-testid="newsletter-submit-btn" type="submit" disabled={sending}
              className="btn-industrial inline-flex items-center justify-center gap-2 !rounded-full bg-[#2E86D1] px-6 py-3 text-sm font-semibold uppercase tracking-wide text-white hover:bg-[#1F6DB2] disabled:opacity-50">
              <PaperPlaneTilt size={16} /> {sending ? "Sūta..." : "Pierakstīties"}
            </button>
          </form>
          <div className="flex items-center gap-3 md:ml-auto">
            {SOCIALS.map((s) => (
              <a key={s.label} href={s.href} target="_blank" rel="noreferrer" aria-label={s.label} data-testid={`social-${s.label.toLowerCase()}`}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1D1D1F] text-white transition-colors hover:bg-[#2E86D1]">
                <s.icon size={20} weight="fill" />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 py-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center gap-3"><Truck size={28} className="text-[#2E86D1]" /><div><p className="font-bold text-sm uppercase tracking-wide">Ātra piegāde</p><p className="text-xs text-slate-400">Visā Latvijā 1–2 darba dienās</p></div></div>
          <div className="flex items-center gap-3"><Invoice size={28} className="text-[#2E86D1]" /><div><p className="font-bold text-sm uppercase tracking-wide">Apmaksa ar rēķinu</p><p className="text-xs text-slate-400">Ērti norēķini juridiskām personām</p></div></div>
          <div className="flex items-center gap-3"><Percent size={28} className="text-[#2E86D1]" /><div><p className="font-bold text-sm uppercase tracking-wide">Apjoma atlaides</p><p className="text-xs text-slate-400">Jo vairāk pērkat, jo zemāka cena</p></div></div>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-4 py-10 grid grid-cols-1 md:grid-cols-4 gap-8">
        <div>
          <span className="inline-block rounded-2xl bg-white px-4 py-2.5">
            <img src="https://customer-assets-lxgj4vgw.emergentagent.net/job_industrial-parts-pro-5/artifacts/eto47ty6_logo.png" alt="DANS G" className="h-8 w-auto" />
          </span>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed">Auto rezerves daļas un auto ķīmija B2B un B2C klientiem. Motoreļļas lielos apjomos lauksaimniekiem, autoparkiem un servisiem.</p>
        </div>
        <div>
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-4">Veikals</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/katalogs" className="hover:text-[#2E86D1]">Viss katalogs</Link></li>
            <li><Link to="/katalogs?category=ella" className="hover:text-[#2E86D1]">Eļļa</Link></li>
            <li><Link to="/katalogs?category=auto-spuldzes" className="hover:text-[#2E86D1]">Auto spuldzes</Link></li>
            <li><Link to="/katalogs?category=autokimija" className="hover:text-[#2E86D1]">Autoķīmija</Link></li>
            <li><Link to="/pasutijuma-statuss" className="hover:text-[#2E86D1]">Pasūtījuma statuss</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-4">Uzņēmums</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/b2b" className="hover:text-[#2E86D1]">B2B sadarbība</Link></li>
            <li><Link to="/atra-pasutisana" className="hover:text-[#2E86D1]">Ātrā pasūtīšana</Link></li>
            <li><Link to="/piedavajums" className="hover:text-[#2E86D1]">Pieprasīt piedāvājumu</Link></li>
            <li><Link to="/blogs" className="hover:text-[#2E86D1]">Blogs</Link></li>
            <li><Link to="/par-mums" className="hover:text-[#2E86D1]">Par mums</Link></li>
            <li><Link to="/kontakti" className="hover:text-[#2E86D1]">Kontakti</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-4">Kontakti</p>
          <ul className="space-y-2 text-sm text-slate-300">
            <li className="flex items-center gap-2"><Phone size={16} /> +371 67 000 000</li>
            <li className="flex items-center gap-2"><EnvelopeSimple size={16} /> info@e-dansg.lv</li>
            <li className="flex items-center gap-2"><MapPin size={16} /> Rīga, Latvija</li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-4 py-6">
          <div data-testid="payment-logos" className="flex flex-wrap items-center gap-2.5">
            {PAYMENTS.map((p) => (
              <span key={p} className="rounded-lg bg-white px-3.5 py-1.5 font-heading text-xs font-extrabold tracking-tight text-[#1D1D1F]">{p}</span>
            ))}
            <span className="mx-2 hidden h-6 w-px bg-white/20 md:block" />
            {CARRIERS.map((c) => (
              <span key={c} className="rounded-lg border border-white/25 px-3.5 py-1.5 font-heading text-xs font-extrabold tracking-tight text-slate-300">{c}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-4 py-4 text-xs text-slate-500">© {new Date().getFullYear()} DANS G. Visas tiesības aizsargātas.</div>
      </div>
    </footer>
  );
}
